import { WALK_SPEED } from "@rietz/shared";

/**
 * The aim offset: where the gun is POINTING, as opposed to where the mouse is.
 * This module owns one pair of small angles and BOTH consumers add them —
 * `loop.ts` sets the camera to `(pitch + offsetPitch, yaw + offsetYaw)` and
 * `fireControl.ts` composes the same sum before spread — so recoil and sway
 * are gameplay rather than viewmodel decoration, and nothing on screen is ever
 * wrong about where the round goes.
 *
 * Recoil is an OFFSET with a spring to zero, not a displacement of
 * `input.pitch`: the mouse hand does not follow the view up, so a permanent
 * kick would steal aim the player has to hand back. `Math.random` is fine
 * here — the server trusts the direction it is handed, and per-client feel is
 * not a desync. Pure maths, no DOM or three.js.
 */

/** What the AIM needs to know about the gun in the player's hands. */
export interface AimWeaponSetup {
  /**
   * The kick scale, 1 = the bare carbine — ALREADY FOLDED with
   * `weaponRecoilMult` by `fireControl.syncWeapon`, the same product the
   * viewmodel gets.
   */
  recoil: number;
  /** Aggregated ergonomics, 0–100. Higher is handier, and steadier. */
  ergonomics: number;
}

/**
 * Seconds for a kick to fall back into the noise. The SMG's 92 ms cadence
 * stacks four inside one recovery and climbs; the carbine's semi-auto cadence
 * mostly settles.
 */
const RECOIL_RECOVERY_SEC = 0.35;

/**
 * Spring rate, rad/s. ωt = 4 leaves ~9 % of a kick — where it stops being
 * distinguishable from sway.
 */
const RECOIL_OMEGA = 4 / RECOIL_RECOVERY_SEC;

/** Per-shot kick for the baseline carbine: ~0.57° up. */
const KICK_PITCH_RAD = 0.01;
/**
 * Horizontal component, uniform in ±this. Zero-mean, so a burst wanders rather
 * than walks — which is why only pitch needs a cap.
 */
const KICK_YAW_RAD = 0.003;

/** Kick scale with the sights up — a shouldered gun is a braced gun. */
const ADS_KICK_SCALE = 0.85;

/**
 * Ceiling on accumulated recoil pitch, ~10°. Safety, not balance: a camera
 * wrapped past vertical is unrecoverable.
 */
const MAX_RECOIL_PITCH_RAD = 0.17;

/** Spring integration substep, seconds. Keeps a 20 fps frame stable. */
const SPRING_STEP = 1 / 120;
const MAX_SPRING_SUBSTEPS = 8;

/**
 * Idle sway half-amplitude, radians — ~0.09°, about 15 cm at 100 m. NOT faded
 * at ADS, unlike the viewmodel's: aimed is when a wandering muzzle is worth
 * something.
 */
const IDLE_AMP_RAD = 0.0015;

/** Ergonomics that sways by exactly `IDLE_AMP_RAD`; the roster's midpoint. */
const ERGO_PIVOT = 50;

/** Sway scale while crouched. Elbows on knees; the classic marksman's trade. */
const CROUCH_SWAY = 0.6;

/** Radians of gait phase per metre travelled — matched to `viewmodel.ts`. */
const BOB_PER_METRE = 1.65;
/** How fast the sway amplitude follows the player's speed, per second. */
const SPEED_BLEND = 6;

/**
 * Gait-locked sway at full walking speed. Yaw leads (a stride swings the
 * shoulders); pitch runs at twice the frequency (two footfalls per stride).
 */
const WALK_YAW_RAD = 0.012;
const WALK_PITCH_RAD = 0.006;
/** Phase lead on the vertical, so the two axes trace a lopsided figure eight
 *  instead of a clean lissajous the eye can learn to read. */
const WALK_PITCH_PHASE = 0.7;

/**
 * What `update` needs from the frame. Callers pass a reusable scratch object —
 * the frame path allocates nothing.
 */
export interface AimContext {
  /** The viewmodel's shoulder blend, 0 = hip, 1 = fully aimed. */
  adsT: number;
  /** Metres the RENDERED player moved since the previous frame. */
  movedM: number;
  crouched: boolean;
}

export class AimSystem {
  /**
   * The composed offsets, radians, rewritten once per `update`. Plain fields so
   * both readers see the identical number.
   */
  offsetPitch = 0;
  offsetYaw = 0;

  /** Recoil bias and its spring velocity, radians and rad/s. */
  private recoilPitch = 0;
  private recoilPitchVel = 0;
  private recoilYaw = 0;
  private recoilYawVel = 0;

  /** `def.recoil × attachments`, or 0 for empty hands — see `setWeapon`. */
  private recoilScale = 0;
  /** Aggregated ergonomics; the pivot value while empty-handed. */
  private ergonomics = ERGO_PIVOT;

  /** Seconds since construction, for the idle sway. */
  private clock = 0;
  /** Gait phase, radians; accumulated from distance travelled. */
  private phase = 0;
  /** Smoothed horizontal speed, m/s. */
  private speed = 0;
  /** Last frame's shoulder blend, so `kick` knows whether the gun is braced. */
  private adsT = 0;

  /**
   * Swaps the gun. `null` is empty hands: no kick, baseline sway. Standing
   * recoil is left to recover — snapping it would flinch on every weapon
   * change.
   */
  setWeapon(setup: AimWeaponSetup | null): void {
    if (setup === null) {
      this.recoilScale = 0;
      this.ergonomics = ERGO_PIVOT;
      return;
    }
    this.recoilScale = setup.recoil > 0 ? setup.recoil : 0;
    const ergo = setup.ergonomics;
    this.ergonomics = Number.isFinite(ergo) ? clamp(ergo, 0, 100) : ERGO_PIVOT;
  }

  /**
   * One trigger pull. The impulse goes on the DISPLACEMENT, not the velocity —
   * the sights must move on the frame the shot was fired.
   */
  kick(): void {
    if (this.recoilScale <= 0) return;
    const scale = this.recoilScale * (1 - (1 - ADS_KICK_SCALE) * this.adsT);

    this.recoilPitch += KICK_PITCH_RAD * scale;
    if (this.recoilPitch > MAX_RECOIL_PITCH_RAD) this.recoilPitch = MAX_RECOIL_PITCH_RAD;
    // Zero-mean: the burst wanders around the aim point; the spring keeps the
    // walk from running away.
    this.recoilYaw += (Math.random() * 2 - 1) * KICK_YAW_RAD * scale;
  }

  /**
   * One rendered frame, called AFTER the trigger is serviced. Runs while dead
   * or in a menu too, so the view is never parked where the last shot left it.
   */
  update(dtMs: number, ctx: AimContext): void {
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;
    this.clock += dt;
    this.adsT = ctx.adsT;

    // --- Speed and gait phase: distance travelled, not time (as in
    // `viewmodel.ts`
    // and `character.ts`), so legs, gun and muzzle share one gait. A teleport
    // must not spin it.
    const step = ctx.movedM > 2 ? 0 : ctx.movedM;
    const instant = dt > 0 ? step / dt : 0;
    this.speed += (instant - this.speed) * Math.min(1, dt * SPEED_BLEND);
    this.phase += step * BOB_PER_METRE;
    if (this.phase > Math.PI * 2) this.phase -= Math.PI * 2;

    // --- Recoil -------------------------------------------------------------
    this.stepRecoil(dt);

    // --- Sway ---------------------------------------------------------------
    const crouch = ctx.crouched ? CROUCH_SWAY : 1;

    // Cheap guns wander: the 30-ergo hunting rifle sways 20 % wider than the
    // 70-ergo SMG.
    const idle = IDLE_AMP_RAD * (1 + (ERGO_PIVOT - this.ergonomics) / 100) * crouch;
    // Four rates sharing no common period, so the pattern never repeats inside
    // a firefight.
    let pitch = (Math.sin(this.clock * 0.53) * 0.6 + Math.sin(this.clock * 0.91) * 0.4) * idle;
    let yaw = (Math.sin(this.clock * 0.41) * 0.6 + Math.sin(this.clock * 0.77) * 0.4) * idle;

    // Rides the SMOOTHED speed, so stopping bleeds the amplitude out instead of
    // cutting it.
    const gait = Math.min(this.speed / WALK_SPEED, 1) * crouch;
    if (gait > 0) {
      yaw += Math.sin(this.phase) * WALK_YAW_RAD * gait;
      pitch += Math.sin(this.phase * 2 + WALK_PITCH_PHASE) * WALK_PITCH_RAD * gait;
    }

    this.offsetPitch = this.recoilPitch + pitch;
    this.offsetYaw = this.recoilYaw + yaw;
  }

  // -----------------------------------------------------------------------

  /**
   * Critically damped spring to zero (ζ = 1 exactly), substepped: no overshoot
   * below the aim point, and stable on a 20 fps frame.
   */
  private stepRecoil(dt: number): void {
    if (
      this.recoilPitch === 0 &&
      this.recoilPitchVel === 0 &&
      this.recoilYaw === 0 &&
      this.recoilYawVel === 0
    ) {
      return;
    }

    const k = RECOIL_OMEGA * RECOIL_OMEGA;
    const c = 2 * RECOIL_OMEGA;
    let remaining = dt;
    for (let i = 0; i < MAX_SPRING_SUBSTEPS && remaining > 0; i++) {
      const h = Math.min(remaining, SPRING_STEP);
      remaining -= h;
      this.recoilPitchVel += (-k * this.recoilPitch - c * this.recoilPitchVel) * h;
      this.recoilPitch += this.recoilPitchVel * h;
      this.recoilYawVel += (-k * this.recoilYaw - c * this.recoilYawVel) * h;
      this.recoilYaw += this.recoilYawVel * h;
    }

    // Settle exactly so an idle gun costs nothing next frame; the epsilons are
    // well under a pixel.
    if (Math.abs(this.recoilPitch) < 1e-5 && Math.abs(this.recoilPitchVel) < 1e-4) {
      this.recoilPitch = 0;
      this.recoilPitchVel = 0;
    }
    if (Math.abs(this.recoilYaw) < 1e-5 && Math.abs(this.recoilYawVel) < 1e-4) {
      this.recoilYaw = 0;
      this.recoilYawVel = 0;
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
